// Sales presets (A4). Each one recreates a current hard-coded backend print so
// it can be edited in the builder:
//   salesOrderA4    <- helper/sale_order_invoice/SaleOrderInvoice.ts  SaleOrderInvoice_1(..., "Sales Order")
//   deliveryNoteA4  <- helper/DN/GRN_1.ts  GRN_1(..., "GRN")   ("Delivery Note")
//   salesInvoiceA4  <- helper/DN/GRN_1.ts  GRN_1(..., "Nope")  ("GST Tax Invoice")
import { text, field, hline, vline, box, col, table, fixedBand, tableBand, a4, preset } from "./helpers";

const W = 194; // A4 content width with 8 mm margins
const FONT = { fontFamily: "Courier New", fontSize: 8 };

// "a, b, c" of the non-blank values (like the TS joinComma / filter(Boolean).join).
const joinNonBlank = (exprs, sep = ", ") => `MID(${exprs.map((e) => `IF(${e}, "${sep}" & ${e}, "")`).join(" & ")}, ${sep.length + 1})`;
// "\n**Label** value" only when value is present.
const optLine = (label, expr, gap = "") => `{{ IF(${expr}, "\n**${label}**${gap}" & ${expr}, "") }}`;
// DL numbers of an address object, e.g. dlList("order.x_pay_to_address", "cmr_pay")
const dlList = (obj, p) => joinNonBlank([`${obj}.${p}_dl_no_20_20b`, `${obj}.${p}_dl_no_21_21b`, `${obj}.${p}_dl_no_21c`, `${obj}.${p}_dl_no_21f`]);
const STORE_DL = joinNonBlank(["store.dl_no_20_20b", "store.dl_no_21_21b", "store.dl_no_21c", "store.dl_no_21f"]);

// GST summary drawn inside a fixed band (one text column per tax field), so it
// can sit to the left of the totals block like the original print.
function gstSummary(x, y, widths, bodyH, { lineColor, headStyle, fallback }) {
  const heads = ["GST%", "Taxable", "SGST", "CGST", "IGST"];
  const exprs = [
    'JOIN(COALESCE(tax.item_gst, "-"), "\n")',
    `JOIN(IF(tax.taxable, FORMAT(tax.taxable, "fixed:2"), "-"), "\n")`,
    `JOIN(IF(ISBLANK(tax.sgst), "-", FORMAT(tax.sgst, "fixed:2")), "\n")`,
    `JOIN(IF(ISBLANK(tax.gst), "-", FORMAT(tax.gst, "fixed:2")), "\n")`,
    `JOIN(IF(ISBLANK(tax.igst), "${fallback}", FORMAT(tax.igst, "fixed:2")), "\n")`,
  ];
  const w = widths.reduce((a, b) => a + b, 0);
  const els = [box(x, y, w, 4.5 + bodyH, { borderWidth: 0.75, borderColor: lineColor }), text(x, y, w, 4.5, "", { bg: headStyle.bg || "" })];
  let cx = x;
  widths.forEach((cw, i) => {
    els.push(text(cx, y, cw, 4.5, heads[i], { ...headStyle, bg: "", bold: true, padding: 0.6, valign: "middle" }));
    els.push(field(cx, y + 4.5, cw, bodyH, exprs[i], "", { padding: 0.6, lineHeight: 1.25 }));
    if (i) els.push(vline(cx, y, 4.5 + bodyH, { color: lineColor }));
    cx += cw;
  });
  els.push(hline(x, y + 4.5, w, { color: lineColor }));
  return els;
}

/* ------------------------------------------------------------------ *
 * Sales Order  (SaleOrderInvoice_1, name = "Sales Order")
 * ------------------------------------------------------------------ */
export function salesOrderA4() {
  const LINE = "#e5e7eb";
  const INK = "#0f172a";
  const L = { color: LINE };
  const SIDE = { bg: "#f1f5f9", color: INK, bold: true, padding: 1, valign: "middle" };
  const CELL = { padding: 1, lineHeight: 1.25, fontSize: 7.5 };
  const pay = "order.sott_pay_to_address";
  const ship = "order.sott_ship_to_address";
  const addr = (o, p) => joinNonBlank(["street", "town", "district", "city", "state", "country", "pincode"].map((k) => `${o}.${p}_address_${k}`), " ");

  // item table column widths (mm); the totals block lines up with them
  const cw = { sno: 5, code: 17, hsn: 15, name: 27, generic: 20, mfr: 19, uom: 12, qty: 8, rate: 14, mrp: 13, disc: 10, tax: 10, total: 24 };
  const LX = 137; // label column starts under MRP (TS: colspan 10 | 3 | 1)
  const LW = 33;
  const VX = 170;
  const VW = 24;
  const RH = 5.5;
  const totals = [
    ["GrossTotal:", "order.sott_sub_total"],
    ["TaxAmount:", "order.sott_total_gst"],
    ["Discount:", "order.sott_total_discount"],
    ["Round Off:", "order.sott_round_off_value"],
    ["TotalAmount:", "order.sott_total_amount"],
  ];

  return preset(
    "sales_order",
    "Sales Order (A4) - current format",
    a4(8),
    [
      fixedBand(
        "Header",
        68,
        [
          // title bar
          text(0, 0, W, 8, "Sales Order", { bg: "#0b3a63", color: "#ffffff", bold: true, fontSize: 13, align: "center", valign: "middle" }),
          // side headings
          text(0, 8, 25, 30, "From", SIDE),
          text(97, 8, 25, 30, "To", SIDE),
          text(0, 38, 25, 30, "Order Details", SIDE),
          text(97, 38, 25, 30, "Shipping", SIDE),
          // From (store)
          text(
            25,
            8,
            72,
            30,
            '**{{ COALESCE(store.company, "-") }}**\n{{ COALESCE(store.company_address, "-") }}' +
              optLine("GST:", "store.company_gstn") +
              optLine("PAN:", "store.pan_number") +
              optLine("FSSAI NO:", "store.fssai_no") +
              optLine("DL NO:", STORE_DL),
            CELL
          ),
          // To (pay-to address of the customer)
          text(
            122,
            8,
            72,
            30,
            `**{{ COALESCE(${pay}.cmr_pay_address_name, cmr_name, "-") }}**\n{{ ${addr(pay, "cmr_pay")} }}` +
              optLine("GST:", `${pay}.cmr_pay_gst`) +
              optLine("PAN:", `${pay}.cmr_pay_pan`) +
              optLine("FSSAI NO:", `${pay}.cmr_pay_fssai_no`) +
              optLine("DL NO:", dlList(pay, "cmr_pay")),
            CELL
          ),
          // Order details
          text(
            25,
            38,
            72,
            30,
            "**Invoice No:** {{ sott_invoice_number | dash }}\n" +
              "**Doc Date:** {{ sott_document_date | date:'DD/MM/YYYY' | dash }}\n" +
              "**Expected/Delivery Date:** {{ sott_delivery_date | date:'DD/MM/YYYY' | dash }}\n" +
              "**Created:** {{ created_date | date:'DD/MM/YYYY' | dash }}  **Updated:** {{ order.update_date | date:'DD/MM/YYYY' | dash }}",
            CELL
          ),
          // Shipping
          text(
            122,
            38,
            72,
            30,
            `**{{ COALESCE(${ship}.cmr_ship_address_name, "-") }}**\n{{ ${addr(ship, "cmr_ship")} }}` +
              optLine("GST:", `${ship}.cmr_ship_gst`) +
              optLine("PAN:", `${ship}.cmr_ship_pan`) +
              optLine("FSSAI NO:", `${ship}.cmr_ship_fssai_no`) +
              optLine("DL NO:", dlList(ship, "cmr_ship")),
            CELL
          ),
          // grid
          box(0, 8, W, 60, { borderWidth: 0.75, borderColor: LINE }),
          hline(0, 38, W, L),
          vline(25, 8, 60, L),
          vline(97, 8, 60, L),
          vline(122, 8, 60, L),
        ],
        { repeat: "top" }
      ),
      tableBand(
        "Items",
        table(
          "items",
          "items",
          0,
          W,
          [
            col("sno", "#", "SNO()", cw.sno, { align: "center" }),
            col("code", "ProductCode", "item_code", cw.code, { format: "dash" }),
            col("hsn", "HsnCode", 'IF(item_hsn == "null", "", item_hsn)', cw.hsn, { format: "dash" }),
            col("name", "ProductName", 'COALESCE(item_name, "-") & IF(item.item_baby_box_packing, "\n" & item.item_baby_box_packing, "")', cw.name, { style: { bold: true, color: INK } }),
            col("generic", "Generic Name", "item_generic_name", cw.generic, { format: "dash" }),
            col("mfr", "Manufacturer Name", "COALESCE(item.mfg_code, item_manufacturer_name)", cw.mfr, { format: "dash" }),
            col("uom", "UomName", "item_uom", cw.uom, { format: "dash" }),
            col("qty", "Qty", "COALESCE(item.item_requested_quantity, item_quantity, item_to_be_delivered, 0)", cw.qty),
            col("rate", "Purchase Rate", "COALESCE(item_unit_price, 0)", cw.rate, { format: "fixed:2" }),
            col("mrp", "MRP", "COALESCE(item.mrp, 0)", cw.mrp, { format: "fixed:2" }),
            col("disc", "Disc%", "COALESCE(item_discount_percentage, 0)", cw.disc, { format: "fixed:2" }),
            col("tax", "Tax%", "COALESCE(item_total_tax_percentage, item_gst, 0)", cw.tax, { format: "fixed:2" }),
            col("total", "Item total", "COALESCE(item_total_amount, 0)", cw.total, { format: "fixed:2" }),
          ],
          {
            style: { fontSize: 7, grid: "all", borderWidth: 0.75, borderColor: LINE, cellPadding: 0.8 },
            headerStyle: { bold: true, bg: "#1e293b", color: "#f8fafc" },
          }
        )
      ),
      fixedBand("Totals", totals.length * RH, [
        box(0, 0, LX, totals.length * RH, { borderWidth: 0.75, borderColor: LINE }),
        gstSummary(1.5, 1, [11, 16, 14, 14, 13.5], totals.length * RH - 6.5, { lineColor: LINE, headStyle: { bg: "#eef2ff", color: INK }, fallback: "0.00" }),
        totals.map(([label, expr], i) => [
          text(LX, i * RH, LW, RH, label, { bg: "#fff7ed", color: INK, bold: true, padding: 1, valign: "middle" }),
          field(VX, i * RH, VW, RH, `COALESCE(${expr}, 0)`, "fixed:2", { bg: "#f0fdf4", color: "#064e3b", bold: true, padding: 1, valign: "middle" }),
          box(LX, i * RH, LW, RH, { borderWidth: 0.75, borderColor: LINE }),
          box(VX, i * RH, VW, RH, { borderWidth: 0.75, borderColor: LINE }),
        ]).flat(),
      ]),
      fixedBand("Footer", 39, [
        text(0, 0, 25, 7, "Remarks", SIDE),
        text(25, 0, W - 25, 7, "{{ remarks | dash }}", { padding: 1, valign: "middle" }),
        box(0, 0, W, 7, { borderWidth: 0.75, borderColor: LINE }),
        vline(25, 0, 7, L),
        box(0, 7, W, 26, { borderWidth: 0.75, borderColor: LINE }),
        text(0, 33, W, 6, "Authorised Signature", { bg: "#f1f5f9", color: "#0ea5e9", bold: true, align: "right", valign: "middle", padding: 1, borderWidth: 0.75, borderColor: LINE }),
      ]),
    ],
    { defaults: { ...FONT, color: "#475569" } }
  );
}

/* ------------------------------------------------------------------ *
 * Delivery Note / GST Tax Invoice  (GRN_1, name = "GRN" | "Nope")
 * ------------------------------------------------------------------ */
function grnFormat({ docType, name, title, p, noLabel, noExpr, dateLabel }) {
  const pay = `order.${p}_pay_to_address`;
  const ship = `order.${p}_ship_to_address`;
  const PAD = { padding: 1, lineHeight: 1.25 };
  const party = (heading, o, k) => {
    const address = joinNonBlank(["street", "town", "city", "state", "pincode"].map((f) => `${o}.cmr_${k}_address_${f}`));
    return (
      `**${heading}**\n**{{ cmr_name | dash }}**` +
      `{{ IF(${o}.cmr_${k}_address_name, "\n" & ${o}.cmr_${k}_address_name, "") }}` +
      `{{ IF(${address}, "\n" & ${address}, "") }}` +
      optLine("FSSAI NO:", `${o}.cmr_${k}_fssai_no`, " ") +
      optLine("DL NO:", dlList(o, `cmr_${k}`), " ") +
      optLine("GST NO:", `${o}.cmr_${k}_gst`, " ") +
      optLine("PAN NO:", `${o}.cmr_${k}_pan`, " ")
    );
  };

  // item table column widths (mm); ProductName is the TS colspan=2 column
  const cw = { sno: 6, code: 14, hsn: 14, name: 27, uom: 11, batch: 15, exp: 17, qty: 8, rate: 13, mrp: 13, sub: 15, taxp: 10, taxamt: 14, total: 17 };
  const LX = 153; // TS: colspan 12 | 2 | 1
  const LW = 24;
  const VX = 177;
  const VW = 17;
  const RH = 5;
  const totals = [
    ["GrossTotal:", `COALESCE(order.${p}_sub_total, 0) - COALESCE(order.${p}_total_discount, 0)`],
    ["TaxAmount:", `COALESCE(order.${p}_total_gst, 0)`],
    ["Discount:", "0"],
    ["Other Charge:", `COALESCE(order.${p}_other_charges, 0)`],
    ["Round Off:", `COALESCE(order.${p}_round_off_value, 0)`],
    ["TotalAmount:", `COALESCE(order.${p}_total_amount, 0)`],
  ];

  return preset(
    docType,
    name,
    a4(8),
    [
      fixedBand(
        "Header",
        70,
        [
          text(0, 0, W, 5.5, title, { bold: true, fontSize: 10, align: "center", valign: "middle" }),
          // From (store)
          text(
            0,
            5.5,
            139,
            30,
            "From,\n**Store Code:**{{ store.store_code | dash }}\n**{{ store.company | dash }}**\n{{ store.company_address | dash }}\n" +
              "**GST NO.:** {{ store.company_gstn | dash }}\n**Pan NO.:** {{ store.pan_number | dash }}\n**FSSAI NO.:** {{ store.fssai_no | dash }}\n" +
              `**D.L. No.:** {{ COALESCE(${STORE_DL}, "-") }}`,
            { ...PAD, fontSize: 7.5 }
          ),
          // document no / date
          text(139, 5.5, 55, 30, `**${noLabel}:** {{ ${noExpr} | dash }}\n**${dateLabel}:** {{ document_date | date:'DD/MM/YYYY' | dash }}\n**Payment Terms:**`, {
            ...PAD,
            valign: "middle",
            lineHeight: 1.6,
          }),
          // Bill To / Ship To
          text(0, 35.5, 92, 34.5, party("Bill To", pay, "pay"), { padding: 1.2, lineHeight: 1.2, fontSize: 7.5 }),
          text(92, 35.5, 102, 34.5, party("Ship To", ship, "ship"), { padding: 1.2, lineHeight: 1.2, fontSize: 7.5 }),
          // grid
          box(0, 0, W, 70),
          hline(0, 5.5, W),
          hline(0, 35.5, W),
          vline(139, 5.5, 30),
          vline(92, 35.5, 34.5),
        ],
        { repeat: "top" }
      ),
      tableBand(
        "Items",
        table(
          "items",
          "batches",
          0,
          W,
          [
            col("sno", "#", "SNO()", cw.sno, { align: "center" }),
            col("code", "Product Code", "TRIM(item_code)", cw.code, { format: "dash" }),
            col("hsn", "Hsn Code", 'IF(item_hsn == "null", "", TRIM(item_hsn))', cw.hsn, { format: "dash" }),
            col("name", "ProductName", "TRIM(item_name)", cw.name, { format: "dash" }),
            col("uom", "Uom Name", "TRIM(item_uom)", cw.uom, { format: "dash" }),
            col("batch", "BatchNo", "TRIM(item_batch_number)", cw.batch, { format: "dash" }),
            col("exp", "Exp", "item_exp_date", cw.exp, { format: "date:YYYY-MM-DD|dash" }),
            col("qty", "Qty", "COALESCE(item.item_sellable_quantity, item_batch_quantity, 0)", cw.qty),
            col("rate", "Rate", "COALESCE(item_batch_sales_rate, 0)", cw.rate, { format: "fixed:2" }),
            col("mrp", "MRP", "COALESCE(item_batch_unit_price, 0)", cw.mrp, { format: "fixed:2" }),
            col("sub", "Subtotal", "COALESCE(item.item_batch_total_base_sales_rate, item_batch_sales_rate * item_batch_quantity, 0)", cw.sub, { format: "fixed:2" }),
            col("taxp", "Tax %", "COALESCE(item_batch_tax_percentage, 0)", cw.taxp, { format: "fixed:2" }),
            col("taxamt", "Tax Amount", "COALESCE(item_batch_tax_amount, 0)", cw.taxamt, { format: "fixed:2" }),
            col("total", "Total Amount", "COALESCE(item.item_batch_total_purchase_rate, item_batch_total_sales_rate, 0)", cw.total, { format: "fixed:2" }),
          ],
          {
            style: { fontSize: 7, grid: "all", borderWidth: 0.75, borderColor: "#000000", cellPadding: 0.8 },
            headerStyle: { bold: true },
          }
        )
      ),
      fixedBand("Totals", totals.length * RH, [
        box(0, 0, LX, totals.length * RH),
        gstSummary(1.5, 1, [12, 18, 15, 15, 16], totals.length * RH - 6.5, { lineColor: "#000000", headStyle: {}, fallback: "-" }),
        totals.map(([label, expr], i) => [
          text(LX, i * RH, LW, RH, label, { padding: 0.8, valign: "middle" }),
          field(VX, i * RH, VW, RH, expr, "fixed:2", { padding: 0.8, valign: "middle" }),
          box(LX, i * RH, LW, RH),
          box(VX, i * RH, VW, RH),
        ]).flat(),
      ]),
      fixedBand("Footer", 54, [
        text(0, 0, W, 6, `Total Amount(in words): {{ COALESCE(order.${p}_total_amount, 0) | words:after }}`, { padding: 0.8, valign: "middle" }),
        text(0, 6, W, 6, "Remarks: {{ remarks }}", { padding: 0.8, valign: "middle" }),
        box(0, 0, W, 6),
        box(0, 6, W, 6),
        box(0, 12, W, 26),
        text(0, 38, W, 9, "from, {{ store.company | dash }}\nAuthorised Signature", { align: "right", valign: "middle", padding: 0.8, borderWidth: 0.75, borderColor: "#000000" }),
        text(0, 49.5, W, 4.5, "Computer Generated Invoice", { align: "center" }),
      ]),
    ],
    { defaults: { ...FONT, color: "#000000" } }
  );
}

export function deliveryNoteA4() {
  return grnFormat({
    docType: "delivery_note",
    name: "Delivery Note (A4) - current format",
    title: "Delivery Note",
    p: "sodtt",
    noLabel: "DN No",
    noExpr: "inv_no",
    dateLabel: "DN Date",
  });
}

export function salesInvoiceA4() {
  return grnFormat({
    docType: "sales_invoice",
    name: "Sales Invoice (A4) - current format",
    title: "GST Tax Invoice",
    p: "soit",
    noLabel: "Bill No",
    noExpr: "soit_custom_invoice_number",
    dateLabel: "Bill Date",
  });
}
